import { usePage } from "@inertiajs/vue3";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useTenantCategories } from "@/Tailwind/Mixins/useCategories";
import { useLocations } from "@/Tailwind/Mixins/useLocations";
import route from "ziggy-js";
import { useUTC } from "@/Tailwind/Mixins/useUTC";
import { includeHours } from "@/Tailwind/Helpers/time";

export const useTaskCreation = () => {
    const { t } = useI18n();
    const { getLocationInformation } = useLocations();
    const loading = ref(false);
    const { toUTC } = useUTC();

    const priorities = usePage().props.priorities?.map((priority) => ({
        label: priority.name,
        value: priority.id,
    }));

    const taskCreationSteps = computed(() => [
        {
            key: "info",
            label: t("tasks.create_task.steps.info.title"),
            description: t("tasks.create_task.steps.info.description"),
            fields: [
                {
                    key: "description",
                    title: t("tasks.create_task.title.title"),
                    placeholder: t("tasks.create_task.title.placeholder"),
                    type: "text",
                    required: true,
                },
                {
                    key: "remarks",
                    title: t("tasks.create_task.description.title"),
                    description: t("tasks.create_task.description.description"),
                    placeholder: t("tasks.create_task.description.placeholder"),
                    type: "textarea",
                },
                {
                    key: "taskMedia",
                    title: t("tasks.create_task.images.title"),
                    description: t("tasks.create_task.images.description"),
                    type: "taskMedia",
                },
            ],
        },
        {
            key: "allocation",
            label: t("tasks.create_task.steps.allocation.title"),
            description: t("tasks.create_task.steps.allocation.description"),
            fields: [
                {
                    title: t("tasks.create_task.tenant.title"),
                    key: "tenant",
                    type: "dropdown",
                    required: true,
                    preFilled: true,
                    options: computed(() =>
                        usePage().props.tenants.map((tenant) => ({
                            label: tenant.name,
                            value: tenant.id,
                        }))
                    ),
                },
                {
                    title: t("tasks.create_task.space.title"),
                    key: "spaces",
                    type: "location",
                    required: true,
                    extraAttributes: {
                        selectorStyle: "dropdown",
                    },
                    injectFormData: {
                        tenants: (form) => {
                            return [form.tenant];
                        },
                        disabled: (form) => {
                            return !form.tenant;
                        },
                    },
                },
            ],
        },
        {
            key: "extra",
            label: t("tasks.create_task.steps.extra.title"),
            description: t("tasks.create_task.steps.extra.description"),
            fields: [
                {
                    key: "priority",
                    title: t("tasks.create_task.priority.title"),
                    type: "dropdown",
                    options: priorities,
                },
                {
                    key: "users",
                    title: t("tasks.create_task.users.title"),
                    type: "users",
                    injectFormData: {
                        tenantId: (form) => {
                            return form.tenant;
                        },
                    },
                },
                {
                    key: "category",
                    title: t("tasks.create_task.category.title"),
                    placeholder: t("tasks.create_task.category.placeholder"),
                    type: "dropdown",
                    injectFormData: {
                        options: (form) => {
                            const spaceId = form.spaces?.id;
                            if (!spaceId) {
                                return [];
                            }

                            const { tenant } = getLocationInformation(spaceId);
                            return useTenantCategories(tenant?.id);
                        },
                    },
                },
                {
                    key: "date_scheduled",
                    title: t("tasks.create_task.date_scheduled.title"),
                    type: "date",
                    extraAttributes: {
                        placement: "top",
                        minDate: new Date(),
                    },
                },
            ],
        },
    ]);

    const handleCreateTask = (inertiaForm, callback = () => {}) => {
        inertiaForm
            .transform((data) => {
                return {
                    ...data,
                    photos: data.taskMedia?.map((image) => image.file),
                    date_scheduled: data.date_scheduled
                        ? toUTC(includeHours(data.date_scheduled).toISOString())
                        : null,
                    assignee_id: data.users || [],
                    space_id: data.spaces?.id,
                    category_id: Array.isArray(data.category)
                        ? data.category?.[0]
                        : data.category,
                };
            })
            .post(route("tasks.add"), {
                onStart: () => {
                    loading.value = true;
                },
                onSuccess: ({ props }) => {
                    callback(props);
                },
                onFinish: () => {
                    loading.value = false;
                },
            });
    };

    return {
        taskCreationSteps,
        handleCreateTask,
        loading,
    };
};
